// AF-3.16 — Experiment Command Center. Sources AF-3.14's own experiment
// memory (which already answers "have we tried this?" and "what did we
// learn?" via AF-3.13's repetition-protection engine) -- never
// recomputes a repeat decision client-side, never uses winner/proven
// language regardless of the underlying result.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { ExperimentCard } from '@/components/brand/ExperimentCard';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { controlPlaneAPI } from '@/lib/intelligenceApi';

export default function ExperimentCommandCenterPage() {
  const ctx = useBrandContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    controlPlaneAPI.experiments(ctx.workspaceId, ctx.entityType, ctx.entityId)
      .then((r) => setData(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  const memory = data?.experiment_memory || [];

  return (
    <DashboardLayout>
      <div data-testid="experiment-command-center-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Experiment Command Center</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">What was tested, what happened, what was learned, and whether repeating it is justified.</p>
        </div>

        <BrandContextBar ctx={ctx} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && memory.length === 0 && (
          <EmptyState
            title="NO EXPERIMENTS YET"
            description="Martian has identified the opportunity, but there is not enough evidence to create an experiment yet."
          />
        )}

        {!loading && !error && memory.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="experiment-memory-list">
            {memory.map((exp) => <ExperimentCard key={exp.experiment_id} experiment={exp} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
